import { Button, Modal, Spin, Table, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { GdLoadingIndicator } from '../../../components/GdLoadingIndicator';
import { GdModal } from '../../../components/GdModal';
import { useRootStore } from '../../../context/storeContext';
import { showLocalDate } from '../../../utils';
import {
  TenantDataDto,
  TenantGlossaryItem,
  TenantGroupItem,
  TenantGuidoItem,
  TenantUserItem,
} from '../stores/orgListStore';

const PAGE_SIZE = 25;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
`;

const LoadingMessage = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 16px 0 8px;
`;

const ScrollableBody = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
`;

type Props = {
  orgId: string;
  orgName: string;
  onClose: () => void;
  onDeleted: () => void;
};

export const OrgDeleteModal = observer(({ orgId, orgName, onClose, onDeleted }: Props) => {
  const { t } = useTranslation();
  const { orgListStore } = useRootStore();
  const [data, setData] = useState<TenantDataDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    orgListStore.transportLayer.fetchTenantData(orgId).then((result) => {
      if (cancelled) return;
      if (result.succeeded) {
        setData(result.data);
      } else {
        notification.error({ message: result.message || 'Failed to load organization data', duration: 5 });
        onClose();
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [orgId, orgListStore.transportLayer, onClose]);

  const handleDelete = useCallback(async () => {
    setShowConfirm(false);
    setDeleting(true);
    const result = await orgListStore.transportLayer.deleteTenantData(orgId, orgName);
    setDeleting(false);
    if (result.succeeded) {
      notification.success({ message: t('admOrgs:DeleteModal:deleteSuccess'), duration: 5 });
      onDeleted();
    } else {
      notification.error({ message: result.message || t('admOrgs:DeleteModal:deleteFailed'), duration: 5 });
    }
  }, [orgId, orgName, orgListStore.transportLayer, t, onDeleted]);

  const userColumns = [
    { title: t('admOrgs:DeleteModal:nameCol'), dataIndex: 'name', key: 'name' },
    { title: t('admOrgs:DeleteModal:emailCol'), dataIndex: 'email', key: 'email' },
    { title: t('admOrgs:DeleteModal:statusCol'), dataIndex: 'status', key: 'status' },
  ];

  const groupColumns = [
    { title: t('admOrgs:DeleteModal:groupNameCol'), dataIndex: 'groupName', key: 'groupName' },
    { title: t('admOrgs:DeleteModal:usersCountCol'), dataIndex: 'numberOfUsers', key: 'numberOfUsers' },
  ];

  const guidoColumns = [
    { title: t('admOrgs:DeleteModal:guidoNameCol'), dataIndex: 'name', key: 'name' },
    {
      title: t('admOrgs:DeleteModal:createdOnCol'),
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (val: string) => showLocalDate(val),
    },
  ];

  const glossaryColumns = [
    { title: t('admOrgs:DeleteModal:wordCol'), dataIndex: 'word', key: 'word' },
  ];

  const isWorking = loading || deleting;

  const bodyContent = loading ? (
    <LoadingContainer>
      <Spin indicator={<GdLoadingIndicator />} />
      <LoadingMessage>{t('admOrgs:DeleteModal:loadingData')}</LoadingMessage>
    </LoadingContainer>
  ) : deleting ? (
    <LoadingContainer>
      <Spin indicator={<GdLoadingIndicator />} />
      <LoadingMessage>{t('admOrgs:DeleteModal:deleting')}</LoadingMessage>
    </LoadingContainer>
  ) : data ? (
    <ScrollableBody>
      <SectionTitle>{t('admOrgs:DeleteModal:usersSection')} ({data.users.length})</SectionTitle>
      <Table<TenantUserItem>
        dataSource={data.users}
        columns={userColumns}
        rowKey="id"
        size="small"
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        locale={{ emptyText: t('admOrgs:DeleteModal:noUsers') }}
      />
      <SectionTitle>{t('admOrgs:DeleteModal:groupsSection')} ({data.groups.length})</SectionTitle>
      <Table<TenantGroupItem>
        dataSource={data.groups}
        columns={groupColumns}
        rowKey="id"
        size="small"
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        locale={{ emptyText: t('admOrgs:DeleteModal:noGroups') }}
      />
      <SectionTitle>{t('admOrgs:DeleteModal:guidosSection')} ({data.guidos.length})</SectionTitle>
      <Table<TenantGuidoItem>
        dataSource={data.guidos}
        columns={guidoColumns}
        rowKey="id"
        size="small"
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        locale={{ emptyText: t('admOrgs:DeleteModal:noGuidos') }}
      />
      <SectionTitle>{t('admOrgs:DeleteModal:glossariesSection')} ({data.glossaries.length})</SectionTitle>
      <Table<TenantGlossaryItem>
        dataSource={data.glossaries}
        columns={glossaryColumns}
        rowKey="id"
        size="small"
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        locale={{ emptyText: t('admOrgs:DeleteModal:noGlossaries') }}
      />
    </ScrollableBody>
  ) : null;

  return (
    <>
      <GdModal
        isVisible
        isClosable={!isWorking}
        title={`${orgName} ${t('admOrgs:DeleteModal:titleSuffix')}`}
        cancelButtonText={t('admOrgs:DeleteModal:cancelBtn')}
        okButtonText={t('admOrgs:DeleteModal:deleteBtn')}
        onCloseModal={isWorking ? undefined : onClose}
        onOkHandler={isWorking || !data ? undefined : () => setShowConfirm(true)}
        disableButtons={isWorking || !data}
        width={900}
      >
        {bodyContent}
      </GdModal>
      <Modal
        centered
        open={showConfirm}
        closable={false}
        footer={null}
        width={420}
      >
        <p style={{ marginBottom: 24 }}>{t('admOrgs:DeleteModal:confirmMessage')}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => setShowConfirm(false)}>
            {t('admOrgs:DeleteModal:confirmCancelBtn')}
          </Button>
          <Button type="primary" danger onClick={handleDelete}>
            {t('admOrgs:DeleteModal:confirmBtn')}
          </Button>
        </div>
      </Modal>
    </>
  );
});
